import { DataTable } from 'components/Shared/DataTable'
import { type GridColDef } from '@mui/x-data-grid'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type PerformanceTrendsWithLocationDtoResponseDto } from 'src/types/api'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { type PerformanceTrendsFilterType } from 'utils/util'
import { Box, Button, Typography } from '@mui/material'
import { PerformanceTrendsExport } from './PerformanceTrendsExport'
import { useSnackbar } from 'stores/useSnackbar'
import ExportIcon from 'assets/export.svg'

type PerformanceTrendsMetricFilter =
  | 'LocationShare'
  | 'CreditsWagered'
  | 'NTI'
  | 'FundsIn'

interface PerformanceTrendsTableProps {
  data: PerformanceTrendsWithLocationDtoResponseDto
  caId: number | undefined
  filterType: PerformanceTrendsFilterType
  selectedMetricFilters: PerformanceTrendsMetricFilter[]
}

interface PerformanceTrendsRow {
  id: string
  license: string
  name: string
  type: string
  [key: string]: string | number | undefined
}

const metricLabelMap: Record<PerformanceTrendsMetricFilter, string> = {
  LocationShare: 'Location Share',
  CreditsWagered: 'Credits Wagered',
  NTI: 'NTI',
  FundsIn: 'Funds In',
}

const metricFieldMap: Record<
  PerformanceTrendsMetricFilter,
  keyof NonNullable<
    NonNullable<
      PerformanceTrendsWithLocationDtoResponseDto['model']['locationSnapshots']
    >[number]['metrics']
  >[number]
> = {
  LocationShare: 'locationShare',
  CreditsWagered: 'creditsWagered',
  NTI: 'nti',
  FundsIn: 'cashIn',
}

const metricSortOrder: Record<PerformanceTrendsMetricFilter, number> = {
  LocationShare: 0,
  CreditsWagered: 1,
  NTI: 2,
  FundsIn: 3,
}

const formatMetricValue = (value: number) => {
  return `$${Math.round(value).toLocaleString()}`
}

const formatMetricCellValue = (value: number | null | undefined) => {
  if (typeof value !== 'number') {
    return '-'
  }

  return formatMetricValue(value)
}

const getCurrentYearMonth = () => {
  const now = new Date()
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  }
}

const isMetricInSelectedPeriod = (
  year: number,
  month: number,
  filterType: PerformanceTrendsFilterType,
  currentYear: number,
  currentMonth: number
) => {
  if (filterType === 'YearToDate') {
    return year === currentYear && month <= currentMonth
  }

  return year === currentYear - 1
}

const buildYearMonthLabel = (year: number, month: number) => {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  if (month < 1 || month > 12) {
    return `${year}/${String(month).padStart(2, '0')}`
  }

  return `${monthNames[month - 1]} ${year}`
}

const buildYearMonthFieldKey = (year: number, month: number) => {
  return `ym_${year}_${String(month).padStart(2, '0')}`
}

const grandTotalFieldKey = 'grandTotal'

interface YearMonth {
  year: number
  month: number
}

const buildYearMonthsFromSnapshots = (
  snapshots: NonNullable<
    PerformanceTrendsWithLocationDtoResponseDto['model']['locationSnapshots']
  >,
  filterType: PerformanceTrendsFilterType,
  currentYearMonth: { year: number; month: number }
) => {
  const yearMonthMap = new Map<string, YearMonth>()

  for (const snapshot of snapshots) {
    for (const metric of snapshot.metrics ?? []) {
      if (!metric.year || !metric.month) {
        continue
      }

      if (
        !isMetricInSelectedPeriod(
          metric.year,
          metric.month,
          filterType,
          currentYearMonth.year,
          currentYearMonth.month
        )
      ) {
        continue
      }

      const key = `${metric.year}-${metric.month}`
      if (!yearMonthMap.has(key)) {
        yearMonthMap.set(key, { year: metric.year, month: metric.month })
      }
    }
  }

  return Array.from(yearMonthMap.values()).sort(
    (a, b) => a.year - b.year || a.month - b.month
  )
}

const collectMetricValues = ({
  metrics,
  selectedMetricField,
  filterType,
  currentYearMonth,
  shouldShowTotalRow,
  selectedMetricFilter,
  selectedMetricForTotal,
  totalByYearMonth,
}: {
  metrics: NonNullable<
    NonNullable<
      PerformanceTrendsWithLocationDtoResponseDto['model']['locationSnapshots']
    >[number]['metrics']
  >
  selectedMetricField: keyof NonNullable<
    NonNullable<
      PerformanceTrendsWithLocationDtoResponseDto['model']['locationSnapshots']
    >[number]['metrics']
  >[number]
  filterType: PerformanceTrendsFilterType
  currentYearMonth: { year: number; month: number }
  shouldShowTotalRow: boolean
  selectedMetricFilter: PerformanceTrendsMetricFilter
  selectedMetricForTotal: PerformanceTrendsMetricFilter
  totalByYearMonth: Map<string, number>
}) => {
  const metricByYearMonth = new Map<string, number>()

  for (const metric of metrics) {
    if (!metric.year || !metric.month) {
      continue
    }

    if (
      !isMetricInSelectedPeriod(
        metric.year,
        metric.month,
        filterType,
        currentYearMonth.year,
        currentYearMonth.month
      )
    ) {
      continue
    }

    const fieldKey = buildYearMonthFieldKey(metric.year, metric.month)
    const metricValue = metric[selectedMetricField]

    if (
      shouldShowTotalRow &&
      selectedMetricFilter === selectedMetricForTotal &&
      typeof metricValue === 'number'
    ) {
      totalByYearMonth.set(
        fieldKey,
        (totalByYearMonth.get(fieldKey) ?? 0) + metricValue
      )
    }

    if (typeof metricValue === 'number') {
      metricByYearMonth.set(fieldKey, metricValue)
    }
  }

  return metricByYearMonth
}

const buildPerformanceTrendsRow = ({
  license,
  name,
  snapshotIndex,
  selectedMetricFilter,
  selectedMetricLabel,
  metricByYearMonth,
  yearMonths,
}: {
  license: string
  name: string
  snapshotIndex: number
  selectedMetricFilter: PerformanceTrendsMetricFilter
  selectedMetricLabel: string
  metricByYearMonth: Map<string, number>
  yearMonths: YearMonth[]
}): PerformanceTrendsRow => {
  const yearMonthCells: Record<string, number | undefined> = {}
  let grandTotal = 0

  for (const { year, month } of yearMonths) {
    const fieldKey = buildYearMonthFieldKey(year, month)
    const value = metricByYearMonth.get(fieldKey)
    yearMonthCells[fieldKey] = value
    grandTotal += value ?? 0
  }

  return {
    id: `${name}-${snapshotIndex}-${selectedMetricFilter}`,
    license,
    name,
    type: selectedMetricLabel,
    ...yearMonthCells,
    [grandTotalFieldKey]: grandTotal,
  }
}

const buildTotalRowCells = (
  yearMonths: YearMonth[],
  totalByYearMonth: Map<string, number>
) => {
  const totalRowCells: Record<string, number> = {}

  for (const { year, month } of yearMonths) {
    const fieldKey = buildYearMonthFieldKey(year, month)
    totalRowCells[fieldKey] = totalByYearMonth.get(fieldKey) ?? 0
  }

  let grandTotal = 0
  for (const value of totalByYearMonth.values()) {
    grandTotal += value
  }

  return {
    totalRowCells,
    grandTotal,
  }
}

export const PerformanceTrendsTable = ({
  data,
  caId,
  filterType,
  selectedMetricFilters,
}: PerformanceTrendsTableProps) => {
  const navigate = useNavigate()
  const setMessage = useSnackbar((state) => state.setMessage)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const toggleModal = () => setIsModalOpen(!isModalOpen)
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const currentYearMonth = getCurrentYearMonth()

  useEffect(() => {
    // If the CA is switched and the new CA does not match then navigate back to the location select page
    if (caId !== selectedCorporateAccountId) {
      void navigate('/GamingReports/PerformanceTrendsTablePage')
    }
  }, [data, caId, selectedCorporateAccountId])

  const yearMonths = useMemo(() => {
    const snapshots = data?.model?.locationSnapshots ?? []
    return buildYearMonthsFromSnapshots(snapshots, filterType, currentYearMonth)
  }, [data, filterType, currentYearMonth.month, currentYearMonth.year])

  const columns = useMemo<Array<GridColDef<PerformanceTrendsRow>>>(() => {
    const baseColumns: Array<GridColDef<PerformanceTrendsRow>> = [
      {
        field: 'name',
        headerName: 'Name',
        flex: 0.5,
        minWidth: 160,
      },
      {
        field: 'type',
        headerName: 'Type',
        flex: 0.6,
        minWidth: 170,
      },
    ]

    const yearMonthColumns: Array<GridColDef<PerformanceTrendsRow>> =
      yearMonths.map(({ year, month }) => ({
        field: buildYearMonthFieldKey(year, month),
        headerName: buildYearMonthLabel(year, month),
        headerAlign: 'right',
        minWidth: 180,
        flex: 0.45,
        align: 'right',
        valueFormatter: ({ value }) => formatMetricCellValue(value as number),
      }))

    const grandTotalColumn: GridColDef<PerformanceTrendsRow> = {
      field: grandTotalFieldKey,
      headerName: 'Grand Total',
      headerAlign: 'right',
      minWidth: 170,
      flex: 0.5,
      align: 'right',
      cellClassName: 'grand-total-cell',
      valueFormatter: ({ value }) => formatMetricCellValue(value as number),
    }

    return [...baseColumns, ...yearMonthColumns, grandTotalColumn]
  }, [yearMonths])

  const rows = useMemo<PerformanceTrendsRow[]>(() => {
    const snapshots = data?.model?.locationSnapshots ?? []

    if (selectedMetricFilters.length === 0) {
      return []
    }

    const shouldShowTotalRow = selectedMetricFilters.length === 1
    const selectedMetricForTotal = selectedMetricFilters[0]
    const totalByYearMonth = new Map<string, number>()
    const rowsWithSortValues: Array<{
      license: string
      name: string
      metricOrder: number
      row: PerformanceTrendsRow
    }> = []

    snapshots.forEach((snapshot, snapshotIndex) => {
      const license = snapshot.license ?? '-'
      const name = snapshot.name ?? '-'
      const metrics = snapshot.metrics ?? []

      for (const selectedMetricFilter of selectedMetricFilters) {
        const selectedMetricLabel = metricLabelMap[selectedMetricFilter]
        const selectedMetricField = metricFieldMap[selectedMetricFilter]
        const metricByYearMonth = collectMetricValues({
          metrics,
          selectedMetricField,
          filterType,
          currentYearMonth,
          shouldShowTotalRow,
          selectedMetricFilter,
          selectedMetricForTotal,
          totalByYearMonth,
        })

        rowsWithSortValues.push({
          license,
          name,
          metricOrder: metricSortOrder[selectedMetricFilter],
          row: buildPerformanceTrendsRow({
            license,
            name,
            snapshotIndex,
            selectedMetricFilter,
            selectedMetricLabel,
            metricByYearMonth,
            yearMonths,
          }),
        })
      }
    })

    const sortedRows = [...rowsWithSortValues]
      .sort(
        (a, b) =>
          a.metricOrder - b.metricOrder ||
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
      )
      .map(({ row }) => row)

    if (!shouldShowTotalRow) {
      return sortedRows
    }

    const { totalRowCells, grandTotal: totalGrandTotal } = buildTotalRowCells(
      yearMonths,
      totalByYearMonth
    )

    const totalRow: PerformanceTrendsRow = {
      id: 'total-row',
      license: '',
      name: '',
      type: 'Totals',
      ...totalRowCells,
      [grandTotalFieldKey]: totalGrandTotal,
    }

    return [totalRow, ...sortedRows]
  }, [
    data,
    currentYearMonth.month,
    currentYearMonth.year,
    selectedMetricFilters,
    yearMonths,
  ])

  return (
    <>
      <Box className="flex lg:justify-end">
        {rows?.length !== 0 && (
          <Button variant="contained" onClick={toggleModal}>
            <img src={ExportIcon} width={15} height={15} alt="Export icon" />
            <Typography variant="label-CTA">Export</Typography>
          </Button>
        )}
      </Box>
      <DataTable
        columns={columns}
        rows={rows}
        sortFieldName="name"
        sort="asc"
        getRowClassName={(params) =>
          params.row.id === 'total-row' ? 'totals-row' : ''
        }
        noDataMessage="No Performance Trends data found"
        getRowHeight={() => 'auto'}
        sx={{
          '& .MuiDataGrid-cell': {
            py: 1,
            alignItems: 'flex-start',
          },
          '& .totals-row .MuiDataGrid-cell': {
            fontWeight: 'bold',
            color: (theme) => theme.palette.text.primary,
          },
          '& .grand-total-cell': {
            fontWeight: 'bold',
            color: (theme) => theme.palette.text.primary,
          },
        }}
      />
      <PerformanceTrendsExport
        isModalOpen={isModalOpen}
        toggleModal={toggleModal}
        setMessage={setMessage}
        data={rows}
        columns={columns}
      />
    </>
  )
}
